import React from "react"
import { DropzoneArea, Text, ProgressBar, Link } from "dash-styled"
import { DropzoneState, useDropzone } from "react-dropzone"
import { routePaths } from "../../../../../../../Routes"
import { InputAndMetadata } from "../../../../../../../Models/Client/JobSubmitData"
import { ParseResult } from "./ParseResult"
import { LinkButton } from "../../../../../LinkButton/LinkButton"
import { JobSubmitDataParseStatus } from "../../../../../../../Models/Client/JobSubmitDataParseStatus"
import { TrimmedWarning } from "../TrimmedWarning"

export type ConvertFn = (args: {
    csv: string
    delimiter?: string | string[]
    headers?: string[]
    maxReportErrorRows: number
}) => Promise<InputAndMetadata[]>

interface Props {
    testId?: string
    single?: boolean
    parseState: JobSubmitDataParseStatus
    onDropFile: (received: File[], rejected: File[]) => void
    onClearFile: () => void
    onDownloadExample: () => void
}

const renderDropZoneContent = (
    { getRootProps, getInputProps, isDragAccept }: DropzoneState,
    onDownloadExample: () => void
): JSX.Element => {
    const downloadSample = (
        event: React.MouseEvent<HTMLSpanElement, MouseEvent>
    ) => {
        event.preventDefault()
        event.stopPropagation()
        onDownloadExample()
    }
    return (
        <div {...getRootProps()}>
            <DropzoneArea
                inputProps={getInputProps()}
                acceptDrop={isDragAccept}
                dragText="Drag your file here"
                dropText="Drop your file here"
                buttonText="Browse"
                className="bg-rhino-100 py-12 rounded border-2 border-dashed border-rhino-500 cursor-pointer"
            >
                <Text size="sm" color="gray">
                    (Please make sure you have the right
                    <br />
                    <LinkButton onClick={downloadSample}>
                        column headings
                    </LinkButton>
                    )
                </Text>
            </DropzoneArea>
        </div>
    )
}

export const CSVInput: React.FC<Props> = ({
    testId,
    parseState,
    single,
    onClearFile,
    onDropFile,
    onDownloadExample
}: Props) => {
    const drop = useDropzone({
        onDrop: onDropFile,
        multiple: false,
        accept: [
            "text/csv",
            "text/plain",
            "text/x-csv",
            "",
            "text/*",
            "application/vnd.ms-excel",
            "application/csv",
            "application/json"
        ],
        disabled: false
    })

    return (
        <div className="mx-6 mt-6" data-testid={testId}>
            {single && <TrimmedWarning className="my-3" />}
            {parseState.type === "inProgress" && (
                <div className="mb-6 mt-8">
                    <ProgressBar
                        fill={parseState.progress}
                        thickness="lg"
                        color="yellow"
                        noMargin={true}
                    />
                    <p className="text-sm text-rhino-900 mt-4 leading-3">
                        Processing
                    </p>
                </div>
            )}
            {parseState.type === "idle" && (
                <div>
                    {renderDropZoneContent(drop, onDownloadExample)}
                    <Text
                        size="sm"
                        color="gray"
                        className="text-right my-4 leading-4"
                    >
                        Max file size: 50MB.{" "}
                        <Link
                            to={routePaths.docs("how-to-format-csv")}
                            target="_blank"
                            className="text-pink-600"
                        >
                            CSV formatting help
                        </Link>
                    </Text>
                </div>
            )}
            <ParseResult
                fileType="csv"
                parseState={parseState}
                onClear={onClearFile}
                onDownloadExample={onDownloadExample}
            />
        </div>
    )
}
